import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { useLocation } from 'react-router-dom'
import SectionNav from '../components/SectionNav'
import { isConsole } from '../lib/surface'
import AccountPlans from '../components/AccountPlans'
import { Button, ConfirmDialog, CopyButton, Field, Pill, Skeleton } from '../components/ui'
import { api, tokens } from '../lib/api'
import { relativeTime } from '../lib/format'
import { useToast } from '../lib/toast'
import type { User } from '../lib/types'
import { useAuth } from '../lib/useAuth'

const MIN_PASSWORD_LENGTH = 10

export default function Account() {
  const { user, signOut, refreshUser } = useAuth()
  const [githubBusy, setGithubBusy] = useState(false)
  const location = useLocation()
  const githubResult = new URLSearchParams(location.search)
  const [githubActionError, setGithubActionError] = useState<string | null>(null)
  const githubError = githubActionError || githubResult.get('github_error')

  const connectGithub = async () => {
    setGithubBusy(true)
    setGithubActionError(null)
    try {
      const result = await api.connectGithub()
      window.location.assign(result.url)
    } catch (error) {
      setGithubActionError(error instanceof Error ? error.message : 'Could not connect GitHub.')
    } finally {
      setGithubBusy(false)
    }
  }

  const disconnectGithub = async () => {
    setGithubBusy(true)
    setGithubActionError(null)
    try {
      await api.disconnectGithub()
      await refreshUser()
    } catch (error) {
      setGithubActionError(error instanceof Error ? error.message : 'Could not disconnect GitHub.')
    } finally {
      setGithubBusy(false)
    }
  }

  if (!user) {
    return (
      <div className="card stack">
        <Skeleton width="30%" height={18} />
        <Skeleton height={38} />
      </div>
    )
  }

  return (
    <>
      <header className="page-head">
        <div className="titles">
          <h1>Account</h1>
          <p className="subtitle tiny">
            {user.email}
            <span className="sep">·</span>
            joined {relativeTime(user.created_at)}
          </p>
        </div>
        <Button variant="ghost" onClick={signOut}>
          Sign out
        </Button>
      </header>

      <SectionNav items={[{id:'profile',label:'Profile'},...(!isConsole ? [{id:'plans',label:'Plans'},{id:'github',label:'GitHub'}] : []),{id:'password',label:'Password'},{id:'two-factor',label:'Two-factor'},{id:'sessions',label:'Sessions'},{id:'data',label:'Data'}]} />
      <section className="stack" id="profile">
        <h2 className="section-title">Profile</h2>
        <Profile
          name={user.name ?? ''}
          phone={user.phone ?? ''}
          onSaved={refreshUser}
        />
      </section>

      {!isConsole && <section id="plans"><AccountPlans /></section>}

      {!isConsole && <section className="stack" id="github">
        <h2 className="section-title">GitHub</h2>
        {githubError && <div className="alert error" role="alert">{githubError}</div>}
        {!githubError && githubResult.has('github_connected') && user.github_login && (
          <div className="alert" role="status">GitHub connected successfully.</div>
        )}
        <div className="card row" style={{ justifyContent: 'space-between' }}>
          <div>
            <strong>
              {user.github_login
                ? `Connected as ${user.github_login}`
                : 'Not connected'}
            </strong>
            <p className="tiny muted">
              {user.github_login
                ? 'Persistent account connection for repository access and push webhooks. Signing out of TalariCloud does not disconnect GitHub or stop automatic deploys.'
                : 'Connect GitHub once to deploy from repositories and keep automatic deploys running independently of your TalariCloud browser session.'}
            </p>
          </div>
          <div className="row">
            <Button variant="primary" loading={githubBusy} onClick={connectGithub}>
              {user.github_login ? 'Reconnect GitHub' : 'Connect GitHub'}
            </Button>
            {user.github_login && (
              <Button disabled={githubBusy} onClick={disconnectGithub}>
                Disconnect
              </Button>
            )}
          </div>
        </div>
      </section>}

      <section className="stack" id="password">
        <h2 className="section-title">Password</h2>
        {user.password_reset_required && (
          <p className="alert warn">Your previous password has been disabled. Sign in with GitHub, then set a new password here within ten minutes. Leave the current password blank.</p>
        )}
        <Password />
      </section>

      <section className="stack" id="two-factor">
        <h2 className="section-title">Two-factor authentication</h2>
        <TwoFactor user={user} onChanged={refreshUser} />
      </section>

      <section className="stack" id="sessions">
        <h2 className="section-title">Sessions</h2>
        <Sessions />
      </section>

      <section className="stack" id="data">
        <h2 className="section-title">Your data</h2>
        <Export />
      </section>

      <section className="stack" id="danger">
        <h2 className="section-title">Danger zone</h2>
        <DeleteAccount email={user.email} onDeleted={signOut} />
      </section>
    </>
  )
}

function Profile({
  name: initialName,
  phone: initialPhone,
  onSaved,
}: {
  name: string
  phone: string
  onSaved: () => Promise<void>
}) {
  const [name, setName] = useState(initialName)
  const [phone, setPhone] = useState(initialPhone)
  const [busy, setBusy] = useState(false)
  const { attempt } = useToast()

  const save = async () => {
    setBusy(true)
    await attempt(
      async () => {
        await api.updateAccount({ name, phone })
        await onSaved()
      },
      { success: 'Profile saved', failure: 'Could not save your profile' },
    )
    setBusy(false)
  }

  return (
    <div className="card card-pad-lg stack">
      <div className="field-row">
        <Field label="Name" htmlFor="acc-name">
          <input
            id="acc-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Phone" htmlFor="acc-phone" hint="Used for Telebirr top-ups.">
          <input
            id="acc-phone"
            placeholder="09xxxxxxxx"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </Field>
      </div>
      <div>
        <Button variant="primary" loading={busy} onClick={save}>
          Save profile
        </Button>
      </div>
    </div>
  )
}

function Password() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [busy, setBusy] = useState(false)
  const { attempt, notify } = useToast()

  const save = async () => {
    if (next.length < MIN_PASSWORD_LENGTH) {
      notify(`Use at least ${MIN_PASSWORD_LENGTH} characters.`, 'error')
      return
    }
    setBusy(true)
    const ok = await attempt(
      async () => {
        // The change signs out every other device. This one is handed a new
        // pair so it carries on without a trip through the login page.
        tokens.set(await api.changePassword(current || null, next))
      },
      {
        success: 'Password changed — every other device has been signed out',
        failure: 'Could not change your password',
      },
    )
    setBusy(false)
    if (ok) {
      setCurrent('')
      setNext('')
    }
  }

  return (
    <div className="card card-pad-lg stack">
      <Field
        label="Current password"
        htmlFor="pw-current"
        hint="Leave blank if you signed up with GitHub and have never set one."
      >
        <input
          id="pw-current"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
      </Field>

      <Field label="New password" htmlFor="pw-new">
        <input
          id="pw-new"
          type="password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          value={next}
          onChange={(event) => setNext(event.target.value)}
        />
      </Field>

      <div>
        <Button variant="primary" loading={busy} disabled={!next} onClick={save}>
          Change password
        </Button>
      </div>
    </div>
  )
}

type TwoFactorStage = 'idle' | 'password' | 'scan' | 'codes' | 'disable'

function TwoFactor({ user, onChanged }: { user: User; onChanged: () => Promise<void> }) {
  const [stage, setStage] = useState<TwoFactorStage>('idle')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [setup, setSetup] = useState<{ secret: string; otpauth_uri: string } | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [recovery, setRecovery] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const { attempt } = useToast()

  useEffect(() => {
    if (!setup) {
      setQr(null)
      return
    }
    // Drawn in the browser: the secret never goes anywhere to be rendered.
    QRCode.toDataURL(setup.otpauth_uri, { width: 192, margin: 1 })
      .then(setQr)
      .catch(() => setQr(null))
  }, [setup])

  const reset = () => {
    setStage('idle')
    setPassword('')
    setCode('')
    setSetup(null)
    setRecovery([])
  }

  const begin = async () => {
    setBusy(true)
    const ok = await attempt(
      async () => {
        setSetup(await api.twoFactorSetup(password || null))
      },
      { success: 'Scan the code with your authenticator app', failure: 'Could not start setup' },
    )
    setBusy(false)
    if (ok) setStage('scan')
  }

  const enable = async () => {
    setBusy(true)
    const ok = await attempt(
      async () => {
        const result = await api.twoFactorEnable(code)
        // Enabling signed every session out; this one carries on with the new pair.
        tokens.set(result.tokens)
        setRecovery(result.recovery_codes)
      },
      {
        success: 'Two-factor authentication is on',
        failure: 'That code did not work',
      },
    )
    setBusy(false)
    if (ok) {
      setStage('codes')
      setCode('')
      await onChanged()
    } else {
      setCode('')
    }
  }

  const disable = async () => {
    setBusy(true)
    const ok = await attempt(() => api.twoFactorDisable(code, password || null), {
      success: 'Two-factor authentication is off',
      failure: 'Could not turn it off',
    })
    setBusy(false)
    if (ok) {
      reset()
      await onChanged()
    } else {
      setCode('')
    }
  }

  if (stage === 'codes') {
    return (
      <div className="card card-pad-lg stack">
        <div>
          <strong>Save your recovery codes</strong>
          <p className="tiny muted">
            Each one signs you in once if you lose your phone. This is the only
            time they are shown — keep them somewhere that is not the phone.
          </p>
        </div>
        <pre className="mono" style={{ margin: 0, lineHeight: 1.8, userSelect: 'all' }}>
          {recovery.join('\n')}
        </pre>
        <div className="row">
          <CopyButton value={recovery.join('\n')} label="Copy codes" size="md" />
          <Button variant="primary" onClick={reset}>
            I have saved them
          </Button>
        </div>
      </div>
    )
  }

  if (user.two_factor_enabled) {
    return (
      <div className="card card-pad-lg stack">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div>
            <strong className="row" style={{ gap: 8 }}>
              On <Pill tone="ok">protected</Pill>
            </strong>
            <p className="tiny muted">
              Signing in asks for a code from your authenticator app after your
              password. Lost the phone? Use a recovery code, then re-enrol here.
            </p>
          </div>
          {stage !== 'disable' && (
            <Button variant="danger" disabled={user.is_admin} onClick={() => setStage('disable')}>
              Turn off
            </Button>
          )}
        </div>
        {user.is_admin && (
          <p className="tiny faint">
            Operator accounts must keep this on. To turn it off, another
            operator has to remove your operator access first.
          </p>
        )}
        {stage === 'disable' && (
          <div className="stack">
            <div className="field-row">
              <Field label="Current password" htmlFor="tf-off-pw" hint="Blank if you signed up with GitHub and never set one.">
                <input
                  id="tf-off-pw"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </Field>
              <Field label="Code from your app" htmlFor="tf-off-code">
                <input
                  id="tf-off-code"
                  className="mono"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </Field>
            </div>
            <div className="row">
              <Button variant="danger" loading={busy} disabled={code.length < 6} onClick={disable}>
                Turn off two-factor
              </Button>
              <Button variant="ghost" onClick={reset}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="card card-pad-lg stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <strong className="row" style={{ gap: 8 }}>
            Off {user.is_admin && <Pill tone="bad">required for operators</Pill>}
          </strong>
          <p className="tiny muted">
            Add a code from an authenticator app (Google Authenticator, Authy,
            1Password…) to every sign-in. A leaked password alone then opens
            nothing.
          </p>
        </div>
        {stage === 'idle' && (
          <Button variant="primary" onClick={() => setStage('password')}>
            Turn on
          </Button>
        )}
      </div>

      {stage === 'password' && (
        <div className="stack">
          <Field
            label="Current password"
            htmlFor="tf-pw"
            hint="Leave blank if you signed up with GitHub and have never set one."
          >
            <input
              id="tf-pw"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>
          <div className="row">
            <Button variant="primary" loading={busy} onClick={begin}>
              Continue
            </Button>
            <Button variant="ghost" onClick={reset}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {stage === 'scan' && setup && (
        <div className="stack">
          <div className="row" style={{ alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' }}>
            {qr ? (
              <img
                src={qr}
                width={192}
                height={192}
                alt="QR code for your authenticator app"
                style={{ borderRadius: 8, background: '#fff', padding: 6 }}
              />
            ) : (
              <Skeleton width={192} height={192} radius={8} />
            )}
            <div className="stack grow" style={{ gap: 10, minWidth: 240 }}>
              <p className="tiny muted" style={{ margin: 0 }}>
                <strong>1.</strong> Scan this with your authenticator app, or
                type the key in by hand:
              </p>
              <div className="row" style={{ gap: 8 }}>
                <code className="mono tiny" style={{ wordBreak: 'break-all' }}>
                  {setup.secret}
                </code>
                <CopyButton value={setup.secret} label="Copy" />
              </div>
              <p className="tiny muted" style={{ margin: 0 }}>
                <strong>2.</strong> Enter the six-digit code the app shows now.
              </p>
              <Field label="Code" htmlFor="tf-code">
                <input
                  id="tf-code"
                  className="mono"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  placeholder="123 456"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </Field>
              <div className="row">
                <Button variant="primary" loading={busy} disabled={code.replace(/\s/g, '').length < 6} onClick={enable}>
                  Turn on two-factor
                </Button>
                <Button variant="ghost" onClick={reset}>
                  Cancel
                </Button>
              </div>
              <p className="tiny faint" style={{ margin: 0 }}>
                Turning it on signs out every other device you are signed in on.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Sessions() {
  const [busy, setBusy] = useState(false)
  const { attempt } = useToast()

  const signOutEverywhereElse = async () => {
    setBusy(true)
    await attempt(
      async () => {
        tokens.set(await api.logoutAll())
      },
      {
        success: 'Every other device has been signed out',
        failure: 'Could not sign out other devices',
      },
    )
    setBusy(false)
  }

  return (
    <div className="card row" style={{ justifyContent: 'space-between' }}>
      <div>
        <strong>Sign out everywhere else</strong>
        <p className="tiny muted">
          Ends every other browser and device signed in to this account —
          a shared computer, an old phone. This one stays signed in.
        </p>
      </div>
      <Button loading={busy} onClick={signOutEverywhereElse}>
        Sign out other devices
      </Button>
    </div>
  )
}

function Export() {
  const [busy, setBusy] = useState(false)
  const { attempt } = useToast()

  const download = async () => {
    setBusy(true)
    await attempt(
      async () => {
        const data = await api.exportAccount()
        // Built in the browser, so the file is never stored on a server.
        const blob = new Blob([JSON.stringify(data, null, 2)], {
          type: 'application/json',
        })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `talaricloud-export-${new Date().toISOString().slice(0, 10)}.json`
        link.click()
        URL.revokeObjectURL(url)
      },
      { success: 'Export downloaded', failure: 'Could not build your export' },
    )
    setBusy(false)
  }

  return (
    <div className="card row" style={{ justifyContent: 'space-between' }}>
      <div>
        <strong>Download everything we hold about you</strong>
        <p className="tiny muted">
          Services, deploys, databases and wallet history as JSON. Secret values
          are excluded — fetch those from each service and database directly.
        </p>
      </div>
      <Button loading={busy} onClick={download}>
        Export data
      </Button>
    </div>
  )
}

function DeleteAccount({
  email,
  onDeleted,
}: {
  email: string
  onDeleted: () => void
}) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const { attempt } = useToast()

  const destroy = async () => {
    setBusy(true)
    const ok = await attempt(() => api.deleteAccount(email), {
      success: 'Your account has been erased',
      failure: 'Could not erase the account',
    })
    setBusy(false)
    setOpen(false)
    if (ok) onDeleted()
  }

  return (
    <>
      <div className="card row" style={{ justifyContent: 'space-between' }}>
        <div>
          <strong>Delete your account</strong>
          <p className="tiny muted">
            Tears down every service, destroys every database and its data, and
            disables sign-in. Audit records are kept in anonymised form, which
            Article 50 of Proclamation No. 1321/2024 requires.
          </p>
        </div>
        <Button variant="danger" onClick={() => setOpen(true)}>
          Delete account
        </Button>
      </div>

      <ConfirmDialog
        open={open}
        destructive
        busy={busy}
        title="Delete your account?"
        requireText={email}
        confirmLabel="Delete my account"
        body={
          <>
            Every service stops and every database is destroyed along with its
            data. This cannot be undone, and there are no backups to restore
            from.
          </>
        }
        onConfirm={destroy}
        onCancel={() => setOpen(false)}
      />
    </>
  )
}



